import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Form, Formik } from "formik";
import { Link } from "react-router-dom";

import { AddIcon, ArrowLongRightIcon, SearchIcon } from "../../assets/icons";
import { Button, Loading, PageTitle, Section } from "../../components";
import { TextInput } from "../../components/forms";
import { useInfiniteQuery } from "@tanstack/react-query";
import useCampaignService from "../../services/api/campaignService";
import dateFormatter from "../../util/dateFormatter";
import { toast } from "react-toastify";
import { enums } from "../../constants/enum";

import "./CampaignsPage.scss";

function CampaignsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const { ref, inView } = useInView();
  const { getCampaigns, queryKey } = useCampaignService();
  const { data, isLoading, error, fetchNextPage, isFetchingNextPage, refetch } =
    useInfiniteQuery({
      queryKey: [queryKey, { searchQuery }],
      queryFn: ({ pageParam = 1 }) => getCampaigns(searchQuery, pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage?.totalPages >= lastPage?.currentPage) {
          return lastPage?.currentPage + 1;
        }
        return undefined;
      },
    });

  const handleSearch = useCallback(
    (query) => {
      if (!query) {
        setSearchQuery("");
      } else {
        setSearchQuery(query);
      }
      refetch();
    },
    [refetch]
  );

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  if (isLoading) {
    return <Loading variant="fullScreen" />;
  }
  if (error) {
    toast.error(error.message || enums.DEFAULT_ERROR_MESSAGE);
  }
  return (
    <div className="page campaigns-page">
      <PageTitle text="Campaigns">
        <Link to="/campaigns/add">
          <Button text="Add New" icon={<AddIcon />} />
        </Link>
      </PageTitle>

      <Section title="Campaign Details" className="campaign-details">
        <div className="campaign-search">
          <Formik
            initialValues={{ query: "" }}
            onSubmit={(values) => handleSearch(values.query)}
          >
            <Form>
              <TextInput
                name="query"
                type="search"
                label="Search by campaign name or location"
                placeholder="Enter campaign name or location"
              />
              <Button text="Search" icon={<SearchIcon />} type="submit" />
            </Form>
          </Formik>
        </div>

        <div className="campaign-list">
          <div className="table-title">
            <h3>Campaigns</h3>
          </div>

          <div className="table-content">
            <table>
              <thead>
                <tr>
                  <th>Campaign Name</th>
                  <th>Location</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.pages.map(({ campaigns }) =>
                  campaigns.map((campaign) => (
                    <tr key={campaign._id}>
                      <td>{campaign.name}</td>
                      <td>{campaign.place}</td>
                      <td>{dateFormatter(campaign.date)}</td>
                      <td>
                        <Link to={`/campaigns/details/${campaign._id}`}>
                          <Button
                            text="View"
                            prefixIcon={<ArrowLongRightIcon />}
                          />
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              ref={ref}
            >
              {isFetchingNextPage ? <Loading variant="inline" /> : null}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

export default CampaignsPage;
